import { Contactpersoon, useDeleteContactpersoon, useGetContactpersonenForTrein } from "@/entities/contactpersoon";
import { NieuwSmsBerichtDialog } from "@/features/berichten/components/nieuw-sms-bericht-dialog";
import { EditContactpersoon, NieuwContactpersoon } from "@/features/contactpersonen";
import { ActionsDropdown, Button, ConfirmDialog, DropdownMenuItem, ErrorDisplay, Skeleton } from "@/shared/components";
import { cn } from "@/shared/utils";
import { useState } from "react";

type MedewerkerToevoegenProps = {
    dossierId: string;
    treinnummer: number;
    className?: string;
};

export function MedewerkerToevoegen({ dossierId, treinnummer, className }: MedewerkerToevoegenProps) {
    const [nieuwDialogOpen, setNieuwDialogOpen] = useState(false);
    return (
        <>
            <Button
                type="button"
                variant="tertiary"
                icon="user-plus"
                title="Medewerker toevoegen"
                iconStrokeWidth="normal"
                size="s"
                iconSize="xs"
                onClick={() => setNieuwDialogOpen(true)}
                className={cn(className)}
            >
                Medewerker toevoegen
            </Button>
            <NieuwContactpersoon
                dossierId={dossierId}
                treinnummer={treinnummer}
                open={nieuwDialogOpen}
                onOpenChange={setNieuwDialogOpen}
            ></NieuwContactpersoon>
        </>
    );
}

type BetrokkenTreinMedewerkerProps = {
    dossierId: string;
    contact: Contactpersoon;
};

function BetrokkenTreinMedewerker({ dossierId, contact }: BetrokkenTreinMedewerkerProps) {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [berichtDialogOpen, setBerichtDialogOpen] = useState(false);

    const deleteContactpersoon = useDeleteContactpersoon();

    return (
        <li className="flex items-center justify-between border-b py-2 last:border-b-0">
            <div>
                <p className="text-content-emphasis">{Contactpersoon.getNaamEnFunctie(contact)}</p>
                <span className="text-content-subtle text-s">{contact.telefoonnummer}</span>
            </div>
            <ActionsDropdown data-testid="betrokken-trein-medewerker">
                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>Bewerken</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setBerichtDialogOpen(true)}>Bericht sturen</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)}>Verwijderen</DropdownMenuItem>
            </ActionsDropdown>
            <EditContactpersoon dossierId={dossierId} contactpersoon={contact} open={editDialogOpen} onOpenChange={setEditDialogOpen} />
            <ConfirmDialog
                key={`${contact.id}-${deleteDialogOpen}`}
                isOpen={deleteDialogOpen}
                onCancel={() => setDeleteDialogOpen(false)}
                onConfirm={async () => {
                    await deleteContactpersoon.mutateAsync({ dossierId, contactpersoonId: contact.id });
                }}
                destructive
                async
            >
                Weet je zeker dat je betrokken medewerker {Contactpersoon.getDisplayName(contact)} wil verwijderen uit dit dossier?
            </ConfirmDialog>
            {berichtDialogOpen && (
                <NieuwSmsBerichtDialog
                    dossierId={dossierId}
                    open={berichtDialogOpen}
                    onOpenChange={setBerichtDialogOpen}
                    prefilledContactIds={[String(contact.id)]}
                />
            )}
        </li>
    );
}

type BetrokkenTreinMedewerkersProps = {
    dossierId: string;
    treinnummer: number;
};
export function BetrokkenTreinMedewerkerToevoegen({ dossierId, treinnummer }: BetrokkenTreinMedewerkersProps) {
    const { data: contactpersonen, isPending, error } = useGetContactpersonenForTrein(dossierId, treinnummer);

    if (isPending) {
        return <Skeleton className="h-5 w-full" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van contactpersonen" />;
    }

    if (contactpersonen.length === 0) {
        return (
            <div className="order-1 col-span-1 flex">
                <MedewerkerToevoegen dossierId={dossierId} treinnummer={treinnummer} />
            </div>
        );
    }
}

export function BetrokkenTreinMedewerkers({ dossierId, treinnummer }: BetrokkenTreinMedewerkersProps) {
    const { data: contactpersonen, isPending, error } = useGetContactpersonenForTrein(dossierId, treinnummer);

    if (isPending) {
        return <Skeleton className="h-5 w-full" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van contactpersonen" />;
    }

    if (contactpersonen.length === 0) {
        // Render nothing, because in this case we will render `<BetrokkenTreinMedewerkerToevoegen />` button
        return null;
    }

    return (
        <div className="border-default flex w-full flex-col gap-2 rounded-l border px-4 py-2">
            <div className="flex items-center justify-between">
                <h4>Medewerkers</h4>
                <MedewerkerToevoegen dossierId={dossierId} treinnummer={treinnummer} />
            </div>
            <ul>
                {contactpersonen.map((contact) => (
                    <BetrokkenTreinMedewerker contact={contact} dossierId={dossierId} key={contact.id} />
                ))}
            </ul>
        </div>
    );
}
